import { AnnotationHelper } from '@showpad/annotation-drawer-js';
import { replaceParam } from 'url-params-helper';
import { isEqual } from 'lodash-es';
import { cancelable } from 'cancelable-promise';
import { capDimensions } from '../../../../utils/math';
import { RenderState } from '../../../../types/RenderState.type';
import { PdfViewerEventType, PdfViewerPreviewRenderedEvent, PdfViewerRenderedPreviewFocusEvent, PdfViewerPageRenderedEvent, PdfViewerRenderedPageFocusEvent, PdfViewerRenderFailedEvent, PdfViewerFocusChangeEvent } from '../../+types/pdf-viewer-event.type';
import { PdfViewerPagePreviewRenderStateChangeEvent, PdfViewerPageLoadStateChangeEvent, PdfViewerPageLayerRenderStateChangeEvent, PdfViewerPageRenderStateChangeEvent, PdfViewerPageDimensionsChangeEvent, PdfViewerPageResetEvent } from './pdf-viewer-page-events.type';
import { PdfViewerPageMapper } from '../../helpers/pdf-viewer-page-mapper';
import { EmbeddedMediaLayerComponent } from '../embedded-media-layer/embedded-media-layer.component';
import { TranslationKeys } from '../../../../types/Translations.type';
import { ErrorMessageComponent } from '../error-message/error-message.component';
import { PdfJsEmbeddedMediaSVGRendererImageExtractorService } from '../../services/pdfjs-embedded-media-svg-renderer-image-extractor.service';
import { LoadState } from '../../+types/load-state.type';
import { TRANSPARENT_IMAGE_SRC } from '../../../../constants/Constants';
import { PdfViewerPageLayer, PdfViewerPageClasses } from './pdf-viewer-page.component.type';
import { PdfJsHelper } from '../../helpers/pdfjs.helper';
export class PdfViewerPage {
    constructor(config, pageConfig, eventBus, pdfJsService, tokenService) {
        this.config = config;
        this.pageConfig = pageConfig;
        this.eventBus = eventBus;
        this.pdfJsService = pdfJsService;
        this.tokenService = tokenService;
        this._previewRenderState = RenderState.Initial;
        this._loadState = LoadState.None;
        this.renderState = RenderState.Initial;
        this._requiredLayers = new Set([
            PdfViewerPageLayer.PdfCanvas,
            PdfViewerPageLayer.PdfText,
            PdfViewerPageLayer.PdfAnnotations,
            PdfViewerPageLayer.PdfEmbeddedMedia,
            PdfViewerPageLayer.Annotations
        ]);
        this.layersRenderState = new Map([
            [PdfViewerPageLayer.PdfCanvas, RenderState.Initial],
            [PdfViewerPageLayer.PdfText, RenderState.Initial],
            [PdfViewerPageLayer.PdfAnnotations, RenderState.Initial],
            [PdfViewerPageLayer.PdfEmbeddedMedia, RenderState.Initial],
            [PdfViewerPageLayer.Annotations, RenderState.Initial]
        ]);
        this.annotationHelper = null;
        this.previewImage = new Image();
        this.pdfCanvasRenderedAtScale = null;
        this.isFocused = false;
        this.disableEmbeddedMediaAutoPlay = false;
        this.maxCanvasPixels = 3000 * 3000;
        this._annotationCanvasMap = null;
        this.dispatchOneRenderedPreviewFocusEvent = (event) => {
            const page = event.payload.pdfViewerPage;
            if (page.id !== this.id) {
                return;
            }
            this.eventBus.dispatch(new PdfViewerRenderedPreviewFocusEvent({
                pdfViewerPage: PdfViewerPageMapper.mapToPublicPdfViewerPage(this)
            }));
            this.eventBus.off(PdfViewerEventType.PreviewRendered, this.dispatchOneRenderedPreviewFocusEvent);
        };
        this.dispatchOneRenderedPageFocusEvent = (event) => {
            const page = event.payload.pdfViewerPage;
            if (page.id !== this.id) {
                return;
            }
            this.eventBus.dispatch(new PdfViewerRenderedPageFocusEvent({
                pdfViewerPage: PdfViewerPageMapper.mapToPublicPdfViewerPage(this)
            }));
            this.eventBus.off(PdfViewerEventType.PageRendered, this.dispatchOneRenderedPageFocusEvent);
        };
        this.createAnnotationHelperElement();
        this.onPdfLinkAnnotationClick = this.onPdfLinkAnnotationClick.bind(this);
    }
    set previewRenderState(value) {
        this._previewRenderState = value;
        this.eventBus.dispatch(new PdfViewerPagePreviewRenderStateChangeEvent({ id: this.id, renderState: this.previewRenderState }));
    }
    get previewRenderState() {
        return this._previewRenderState;
    }
    set loadState(value) {
        this._loadState = value;
        this.eventBus.dispatch(new PdfViewerPageLoadStateChangeEvent({ pageId: this.id, loadState: this.loadState }));
    }
    get loadState() {
        return this._loadState;
    }
    updateRenderState() {
        const renderState = this.calculateRenderState();
        if (this.renderState !== renderState) {
            this.renderState = renderState;
            this.eventBus.dispatch(new PdfViewerPageRenderStateChangeEvent({ id: this.id, renderState }));
            if (renderState === RenderState.Finished) {
                this.eventBus.dispatch(new PdfViewerPageRenderedEvent({
                    pdfViewerPage: PdfViewerPageMapper.mapToPublicPdfViewerPage(this)
                }));
            }
            if (renderState === RenderState.Failed) {
                this.eventBus.dispatch(new PdfViewerRenderFailedEvent({
                    pdfViewerPage: PdfViewerPageMapper.mapToPublicPdfViewerPage(this)
                }));
            }
        }
    }
    set requiredLayers(value) {
        const requiredLayers = new Set(value);
        if (!requiredLayers.has(PdfViewerPageLayer.PdfCanvas)) {
            this.destroyPdfCanvas();
        }
        if (!requiredLayers.has(PdfViewerPageLayer.PdfText)) {
            this.destroyPdfText();
        }
        if (!requiredLayers.has(PdfViewerPageLayer.PdfAnnotations)) {
            this.destroyPdfAnnotations();
        }
        if (!requiredLayers.has(PdfViewerPageLayer.Annotations)) {
            this.destroyAnnotations();
        }
        if (!requiredLayers.has(PdfViewerPageLayer.PdfEmbeddedMedia)) {
            this.destroyPdfEmbeddedMedia();
        }
        if (requiredLayers.has(PdfViewerPageLayer.PdfEmbeddedMedia) && !this.embeddedData) {
            requiredLayers.delete(PdfViewerPageLayer.PdfEmbeddedMedia);
        }
        this._requiredLayers = requiredLayers;
        this.updateRenderState();
    }
    get requiredLayers() {
        return this._requiredLayers;
    }
    get requiredLayersRenderState() {
        const layersRenderState = new Map();
        this.requiredLayers.forEach((pageLayer) => layersRenderState.set(pageLayer, this.layersRenderState.get(pageLayer)));
        return layersRenderState;
    }
    calculateRenderState() {
        const renderStates = [];
        this.requiredLayers.forEach((pageLayer) => renderStates.push(this.layersRenderState.get(pageLayer)));
        if (!renderStates.length) {
            return RenderState.Finished;
        }
        if (renderStates.every(renderState => renderState === RenderState.Initial)) {
            return RenderState.Initial;
        }
        if (renderStates.every(renderState => renderState === RenderState.Finished)) {
            return RenderState.Finished;
        }
        if (renderStates.some(renderState => renderState === RenderState.Failed)) {
            return RenderState.Failed;
        }
        return RenderState.Running;
    }
    updateLayersRenderState(layer, renderState) {
        this.layersRenderState.set(layer, renderState);
        this.eventBus.dispatch(new PdfViewerPageLayerRenderStateChangeEvent({ id: this.id, pageNumber: this.pageNumber, layer, renderState }));
        this.updateRenderState();
    }
    get id() {
        return this.pageConfig.id;
    }
    get pageNumber() {
        return this.pageConfig.pageNumber;
    }
    get previewUrl() {
        return this.pageConfig.previewUrl;
    }
    get annotations() {
        if (this.annotationHelper) {
            const annotations = this.annotationHelper.getData();
            if (!annotations.length) {
                return null;
            }
            return annotations;
        }
        return this.pageConfig.annotations;
    }
    get embeddedData() {
        return this.pageConfig.embeddedData;
    }
    get pageElementWidth() {
        if (this.pageElement) {
            return this.pageElement.clientWidth;
        }
    }
    get pageElementHeight() {
        if (this.pageElement) {
            return this.pageElement.clientHeight;
        }
    }
    set dimensions(value) {
        this._dimensions = value;
    }
    get dimensions() {
        return this._dimensions || this.pageConfig.dimensions;
    }
    get scale() {
        return this.pageElementWidth / this.dimensions.width;
    }
    renderPreview() {
        if (this.previewRenderState !== RenderState.Initial) {
            return;
        }
        this.previewRenderState = RenderState.Running;
        const scaledPreviewUrl = replaceParam('size', '1600x1600', this.previewUrl); // add util to get variation
        this.previewImage.onload = () => {
            this.previewRenderState = RenderState.Finished;
            this.eventBus.dispatch(new PdfViewerPreviewRenderedEvent({ pdfViewerPage: PdfViewerPageMapper.mapToPublicPdfViewerPage(this) }));
            this.pageElement.classList.add('sp-pdf-viewer-page--preview-rendered');
        };
        this.previewImage.onerror = error => {
            this.previewRenderState = RenderState.Failed;
        };
        this.pageElement.style.backgroundImage = `url(${scaledPreviewUrl})`;
        this.previewImage.src = scaledPreviewUrl;
    }
    destroyPreview() {
        this.previewImage.onload = null;
        this.previewImage.onerror = null;
        this.previewImage.src = TRANSPARENT_IMAGE_SRC;
        if (this.pageElement) {
            this.pageElement.style.backgroundImage = null;
        }
        this.previewRenderState = RenderState.Initial;
    }
    load() {
        if (this.loadState !== LoadState.None) {
            return;
        }
        this.loadState = LoadState.Loading;
        this.loadPromise = cancelable(new Promise(resolve => {
            this.loadPdfPage()
                .then(() => this.ensurePdfEmbeddedMediaDependencies())
                .then(() => {
                this.loadState = LoadState.Finished;
                this.destroyErrorMessage();
                resolve();
            })
                .catch(() => {
                this.loadState = LoadState.Failed;
                this.renderErrorMessage();
                resolve();
            });
        }));
    }
    loadPdfPage() {
        if (this.loadPdfPagePromise) {
            return this.loadPdfPagePromise;
        }
        this.loadPdfPagePromise = cancelable(new Promise((resolve, reject) => {
            return this.pdfJsService
                .getPage(this.pageNumber)
                .then(pdfPage => {
                this.pdfPage = pdfPage;
                const dimensions = PdfJsHelper.calculatePageDimensions(this.pdfPage);
                if (!isEqual(dimensions, this.dimensions)) {
                    this.dimensions = dimensions;
                    this.eventBus.dispatch(new PdfViewerPageDimensionsChangeEvent({ pageId: this.id }));
                }
                resolve();
            })
                .catch(error => {
                this.loadPdfPagePromise = null;
                reject();
            });
        }));
        return this.loadPdfPagePromise;
    }
    ensurePdfEmbeddedMediaDependencies() {
        if (!this.embeddedData) {
            return cancelable(Promise.resolve());
        }
        if (this.ensurePdfEmbeddedMediaDependenciesPromise) {
            return this.ensurePdfEmbeddedMediaDependenciesPromise;
        }
        this.imageExtractorService = new PdfJsEmbeddedMediaSVGRendererImageExtractorService(this.pdfJsService.pdfjsLib, this.pdfPage, this.embeddedData.media);
        this.ensurePdfEmbeddedMediaDependenciesPromise = cancelable(new Promise((resolve, reject) => {
            this.imageExtractorService.render().then((extractedImages) => {
                this.pdfPageExtractedImages = extractedImages;
                this.embeddedData.media.forEach((mediaItem, i) => {
                    mediaItem.renderIndex = null;
                    const extractedImage = extractedImages[i];
                    if (extractedImage) {
                        mediaItem.renderIndex = extractedImage.index;
                    }
                });
                this.imageExtractorService.destroy();
                this.imageExtractorService = null;
                resolve();
            }, () => {
                this.ensurePdfEmbeddedMediaDependenciesPromise = null;
                this.imageExtractorService.destroy();
                this.imageExtractorService = null;
                reject();
            });
        }));
        return this.ensurePdfEmbeddedMediaDependenciesPromise;
    }
    renderPdfCanvas() {
        this.updateLayersRenderState(PdfViewerPageLayer.PdfCanvas, RenderState.Running);
        this.pdfCanvasRenderedAtScale = this.scale;
        if (this.renderPdfCanvasTask) {
            this.renderPdfCanvasTask.cancel();
            this.renderPdfCanvasTask = null;
        }
        const pixelRatio = window.devicePixelRatio;
        const cappedDimensions = capDimensions(this.pageElementWidth * pixelRatio, this.pageElementHeight * pixelRatio, this.maxCanvasPixels);
        const scale = cappedDimensions.width / this.dimensions.width;
        const viewport = this.pdfPage.getViewport({ scale });
        const canvas = document.createElement('canvas');
        const canvasContext = canvas.getContext('2d', {
            willReadFrequently: true
        });
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const renderContext = {
            canvasContext,
            viewport,
            annotationCanvasMap: this.annotationCanvasMap
        };
        this.renderPdfCanvasTask = this.pdfPage.render(renderContext);
        this.renderPdfCanvasTask.promise
            .then(() => {
            /*
            canvasContext.beginPath();
            canvasContext.rect(0, 0, canvas.width, canvas.height);
            canvasContext.fillStyle = '#' + (((1 << 24) * Math.random()) | 0).toString(16);
            canvasContext.fill();
            */
            this.pdfCanvasLayerWrapperElement.innerHTML = '';
            this.pdfCanvasLayerWrapperElement.appendChild(canvas);
            this.pdfCanvasLayerElement = canvas;
            this.pageElement.classList.add('sp-pdf-viewer-page--pdf-canvas-rendered');
            this.updateLayersRenderState(PdfViewerPageLayer.PdfCanvas, RenderState.Finished);
        })
            .catch(error => {
            if (error.type === 'canvas') {
                console.log('PfdJsService :: error instanceof RenderingCancelledException');
                return;
            }
            console.error(error);
            this.updateLayersRenderState(PdfViewerPageLayer.PdfCanvas, RenderState.Failed);
        });
    }
    destroyPdfCanvas() {
        if (this.renderPdfCanvasTask) {
            this.renderPdfCanvasTask.cancel();
            this.renderPdfCanvasTask = null;
        }
        if (this.pdfCanvasLayerWrapperElement) {
            this.pdfCanvasLayerWrapperElement.innerHTML = '';
        }
        if (this.pdfCanvasLayerElement) {
            this.pdfCanvasLayerElement = null;
        }
        this.pdfCanvasRenderedAtScale = null;
        this.updateLayersRenderState(PdfViewerPageLayer.PdfCanvas, RenderState.Initial);
    }
    renderPdfEmbeddedMedia() {
        if (this.embeddedData) {
            this.updateLayersRenderState(PdfViewerPageLayer.PdfEmbeddedMedia, RenderState.Running);
            if (!this.pdfEmbeddedMediaLayerComponent) {
                this.pdfEmbeddedMediaLayerComponent = new EmbeddedMediaLayerComponent(JSON.parse(JSON.stringify(this.embeddedData)), // needs to be immutable spread operator not enough
                this.pdfEmbeddedMediaLayerWrapperElement, this.pdfJsService.pdfjsLib, this.pdfPage, this.pdfPageExtractedImages, this.disableEmbeddedMediaAutoPlay, this.maxCanvasPixels);
            }
            this.pdfEmbeddedMediaLayerComponent.render().then(() => {
                this.updateLayersRenderState(PdfViewerPageLayer.PdfEmbeddedMedia, RenderState.Finished);
            }, () => {
                this.updateLayersRenderState(PdfViewerPageLayer.PdfEmbeddedMedia, RenderState.Failed);
            });
        }
        else {
            this.updateLayersRenderState(PdfViewerPageLayer.PdfEmbeddedMedia, RenderState.Finished);
        }
    }
    resizePdfEmbeddedMedia() {
        if (this.pdfEmbeddedMediaLayerComponent) {
            this.pdfEmbeddedMediaLayerComponent.resize();
        }
    }
    destroyPdfEmbeddedMedia() {
        if (this.pdfEmbeddedMediaLayerComponent) {
            this.pdfEmbeddedMediaLayerComponent.destroy();
            this.pdfEmbeddedMediaLayerComponent = null;
        }
        this.updateLayersRenderState(PdfViewerPageLayer.PdfEmbeddedMedia, RenderState.Initial);
    }
    renderPdfText() {
        if (this.layersRenderState.get(PdfViewerPageLayer.PdfText) !== RenderState.Initial) {
            return;
        }
        this.updateLayersRenderState(PdfViewerPageLayer.PdfText, RenderState.Running);
        const textLayerElement = document.createElement('div');
        const viewport = this.pdfPage.getViewport({ scale: 1 });
        textLayerElement.classList.add('pdf-text-layer-element');
        textLayerElement.classList.add('textLayer');
        textLayerElement.style.width = `${viewport.width}px`;
        textLayerElement.style.height = `${viewport.height}px`;
        this.pdfTextLayerWrapperElement.innerHTML = '';
        this.pdfTextLayerWrapperElement.appendChild(textLayerElement);
        this.pdfTextLayerElement = textLayerElement;
        this.pdfPageGetTextContentPromise = cancelable(this.pdfPage.getTextContent());
        this.pdfPageGetTextContentPromise
            .then(textContent => {
            const textLayerRenderParameters = {
                textContent: textContent,
                container: textLayerElement,
                viewport: viewport,
                textDivs: []
            };
            this.textLayerRenderTask = this.pdfJsService.pdfjsLib.renderTextLayer(textLayerRenderParameters);
            return this.textLayerRenderTask.promise;
        })
            .then(() => {
            this.resizePdfText();
            this.updateLayersRenderState(PdfViewerPageLayer.PdfText, RenderState.Finished);
        })
            .catch(error => {
            console.error(error);
            this.updateLayersRenderState(PdfViewerPageLayer.PdfText, RenderState.Failed);
        });
    }
    resizePdfText() {
        if (this.pdfTextLayerElement && this.pageElement) {
            this.pdfTextLayerElement.style.transform = `scale(${this.scale})`;
        }
    }
    destroyPdfText() {
        if (this.pdfPageGetTextContentPromise) {
            this.pdfPageGetTextContentPromise.cancel();
            this.pdfPageGetTextContentPromise = null;
        }
        if (this.textLayerRenderTask) {
            this.textLayerRenderTask.cancel();
            this.textLayerRenderTask = null;
        }
        if (this.pdfTextLayerWrapperElement) {
            this.pdfTextLayerWrapperElement.innerHTML = '';
        }
        this.pdfTextLayerElement = null;
        this.updateLayersRenderState(PdfViewerPageLayer.PdfText, RenderState.Initial);
    }
    repaintPdfCanvas() {
        // This is a workaround for what seems to be a Safari only issue: https://jira.showpad.io/browse/SP-40443
        // When the user is zooming in it can happen that at a certain zoom level Safari is no longer rendering the PDF canvas correctly.
        // As a fix we change the styling of the canvas to force the browser to repaint the canvas every time the page is resized.
        if (this.pdfCanvasLayerElement) {
            this.pdfCanvasLayerElement.style.setProperty('transform', 'translateZ(0)');
            requestAnimationFrame(() => {
                if (this.pdfCanvasLayerElement) {
                    this.pdfCanvasLayerElement.style.removeProperty('transform');
                }
            });
        }
    }
    renderPdfAnnotations() {
        if (this.layersRenderState.get(PdfViewerPageLayer.PdfAnnotations) !== RenderState.Initial) {
            return;
        }
        this.updateLayersRenderState(PdfViewerPageLayer.PdfAnnotations, RenderState.Running);
        this.pdfPageGetAnnotationsPromise = cancelable(this.pdfPage.getAnnotations({ intent: 'display' }));
        this.pdfPageGetAnnotationsPromise
            .then(annotations => {
            if (annotations && annotations.length) {
                const viewport = this.pdfPage.getViewport({ scale: 1 });
                const annotationLayerElement = document.createElement('div');
                annotationLayerElement.classList.add('pdf-annotation-layer-element');
                annotationLayerElement.classList.add('annotationLayer');
                annotationLayerElement.style.width = `${viewport.width}px`;
                annotationLayerElement.style.height = `${viewport.height}px`;
                const annotationLayerParameters = {
                    viewport: viewport.clone({
                        dontFlip: true
                    }),
                    div: annotationLayerElement,
                    annotations,
                    page: this.pdfPage,
                    imageResourcesPath: '/images/',
                    renderForms: false,
                    linkService: this.pdfJsService.pdfJsLinkService,
                    downloadManager: null,
                    annotationCanvasMap: this.annotationCanvasMap
                };
                this.pdfJsService.pdfjsLib.AnnotationLayer.render(annotationLayerParameters);
                this.pdfAnnotationLayerWrapperElement.innerHTML = '';
                this.pdfAnnotationLayerWrapperElement.appendChild(annotationLayerElement);
                this.pdfAnnotationLayerElement = annotationLayerElement;
                this.resizePdfAnnotations();
            }
            this.updateLayersRenderState(PdfViewerPageLayer.PdfAnnotations, RenderState.Finished);
        })
            .catch(error => {
            console.error(error);
            this.updateLayersRenderState(PdfViewerPageLayer.PdfAnnotations, RenderState.Failed);
        });
    }
    resizePdfAnnotations() {
        if (this.pdfAnnotationLayerElement && this.pageElement) {
            this.pdfAnnotationLayerElement.style.transform = `scale(${this.scale})`;
        }
    }
    destroyPdfAnnotations() {
        if (this.pdfPageGetAnnotationsPromise) {
            this.pdfPageGetAnnotationsPromise.cancel();
            this.pdfPageGetAnnotationsPromise = null;
        }
        if (this.pdfAnnotationLayerWrapperElement) {
            this.pdfAnnotationLayerWrapperElement.removeEventListener('click', this.onPdfLinkAnnotationClick);
            this.pdfAnnotationLayerWrapperElement.innerHTML = '';
        }
        this.pdfAnnotationLayerElement = null;
        this.updateLayersRenderState(PdfViewerPageLayer.PdfAnnotations, RenderState.Initial);
    }
    set annotationCanvasMap(value) {
        this._annotationCanvasMap = value;
    }
    get annotationCanvasMap() {
        if (!this._annotationCanvasMap) {
            this._annotationCanvasMap = new Map();
        }
        return this._annotationCanvasMap;
    }
    renderPageElement(parentElement) {
        if (!parentElement) {
            return;
        }
        this.parentElement = parentElement;
        if (!this.pageElement) {
            this.pageElement = document.createElement('div');
            this.pageElement.classList.add(PdfViewerPageClasses.Page);
            this.pdfCanvasLayerWrapperElement = document.createElement('div');
            this.pdfCanvasLayerWrapperElement.classList.add(PdfViewerPageClasses.CanvasLayer);
            this.pageElement.appendChild(this.pdfCanvasLayerWrapperElement);
            this.pdfEmbeddedMediaLayerWrapperElement = document.createElement('div');
            this.pdfEmbeddedMediaLayerWrapperElement.classList.add(PdfViewerPageClasses.MediaLayer);
            this.pageElement.appendChild(this.pdfEmbeddedMediaLayerWrapperElement);
            this.pdfTextLayerWrapperElement = document.createElement('div');
            this.pdfTextLayerWrapperElement.classList.add(PdfViewerPageClasses.TextLayer);
            this.pageElement.appendChild(this.pdfTextLayerWrapperElement);
            this.pdfAnnotationLayerWrapperElement = document.createElement('div');
            this.pdfAnnotationLayerWrapperElement.classList.add(PdfViewerPageClasses.PdfAnnotationLayer);
            this.pageElement.appendChild(this.pdfAnnotationLayerWrapperElement);
            this.pdfAnnotationLayerWrapperElement.addEventListener('click', this.onPdfLinkAnnotationClick);
            this.annotationLayerWrapperElement = document.createElement('div');
            this.annotationLayerWrapperElement.classList.add(PdfViewerPageClasses.AnnotationLayer);
            this.annotationLayerWrapperElement.appendChild(this.annotationHelperElement);
            this.pageElement.appendChild(this.annotationLayerWrapperElement);
        }
        if (this.pageElement.parentElement) {
            return;
        }
        this.parentElement.appendChild(this.pageElement);
    }
    getAnnotationHelper() {
        if (!this.annotationHelper) {
            try {
                this.createAnnotationHelper();
            }
            catch (_a) { }
        }
        return this.annotationHelper;
    }
    didAnnotationsChange() {
        if (!this.annotationHelper) {
            return false;
        }
        return this.annotationHelper.didAnnotationsChange();
    }
    getAnnotations() {
        return this.annotations;
    }
    createAnnotationHelperElement() {
        this.annotationHelperElement = document.createElement('div');
        this.annotationHelperElement.classList.add('sp-pdf-viewer-page__annotation-helper-element');
    }
    createAnnotationHelper() {
        if (this.annotationHelper) {
            return;
        }
        const annotations = this.annotations || [];
        this.annotationHelper = new AnnotationHelper(this.annotationHelperElement, {
            meta: {},
            data: [...annotations]
        }, {
            create: this.pageConfig.canCreateAnnotations,
            creatorZoom: this.scale,
            append: true
        });
        if (this.annotationHelper.creator) {
            this.annotationHelper.creator.setIsEnabled(false);
        }
    }
    get hasAnnotations() {
        return this.annotations && !!this.annotations.length;
    }
    renderAnnotations() {
        if (this.hasAnnotations) {
            if (this.layersRenderState.get(PdfViewerPageLayer.Annotations) !== RenderState.Initial) {
                return;
            }
            this.updateLayersRenderState(PdfViewerPageLayer.Annotations, RenderState.Running);
            try {
                this.createAnnotationHelper();
                this.resizeAnnotations();
                this.updateLayersRenderState(PdfViewerPageLayer.Annotations, RenderState.Finished);
            }
            catch (error) {
                console.error(error);
                this.updateLayersRenderState(PdfViewerPageLayer.Annotations, RenderState.Failed);
            }
        }
        else {
            this.updateLayersRenderState(PdfViewerPageLayer.Annotations, RenderState.Finished);
        }
    }
    resizeAnnotations() {
        if (this.annotationHelper) {
            this.annotationHelper.resize(this.scale);
        }
    }
    destroyAnnotations() {
        if (this.annotationLayerWrapperElement) {
            this.annotationLayerWrapperElement.innerHTML = '';
        }
        this.updateLayersRenderState(PdfViewerPageLayer.Annotations, RenderState.Initial);
    }
    resetAnnotations() {
        if (this.annotationHelper) {
            this.annotationHelper.reset();
        }
    }
    resize() {
        if (!this.pdfPage || !this.pageElement) {
            return;
        }
        this.repaintPdfCanvas();
        this.resizePdfText();
        this.resizePdfAnnotations();
        this.resizePdfEmbeddedMedia();
        this.resizeAnnotations();
    }
    enableAnnotations() {
        if (this.annotationHelper && this.pageElement) {
            this.annotationHelper.creator.setIsEnabled(true);
            this.pageElement.classList.add(PdfViewerPageClasses.IsAnnotating);
        }
        if (this.pdfEmbeddedMediaLayerComponent) {
            this.pdfEmbeddedMediaLayerComponent.pauseMedia();
        }
    }
    disableAnnotations() {
        if (this.annotationHelper && this.pageElement) {
            this.annotationHelper.creator.setIsEnabled(false);
            this.pageElement.classList.remove(PdfViewerPageClasses.IsAnnotating);
        }
    }
    destroyPageElement() {
        if (this.pageElement && this.pageElement.parentElement) {
            this.pageElement.parentElement.removeChild(this.pageElement);
        }
        this.pageElement = null;
    }
    reset() {
        if (this.loadPromise) {
            this.loadPromise.cancel();
            this.loadPromise = null;
        }
        if (this.loadPdfPagePromise) {
            this.loadPdfPagePromise.cancel();
            this.loadPdfPagePromise = null;
        }
        if (this.ensurePdfEmbeddedMediaDependenciesPromise) {
            this.ensurePdfEmbeddedMediaDependenciesPromise.cancel();
            this.ensurePdfEmbeddedMediaDependenciesPromise = null;
        }
        if (this.imageExtractorService) {
            this.imageExtractorService.destroy();
            this.imageExtractorService = null;
        }
        if (this.pdfPage) {
            this.pdfPage.cleanup();
            this.pdfPage = null;
        }
        this.destroyPreview();
        this.destroyPdfCanvas();
        this.destroyPdfText();
        this.destroyPdfAnnotations();
        this.destroyPdfEmbeddedMedia();
        this.destroyAnnotations();
        this.destroyErrorMessage();
        this.destroyPageElement();
        this.blur();
        this.loadState = LoadState.None;
        this.annotationCanvasMap = null;
        this.eventBus.dispatch(new PdfViewerPageResetEvent({ pageNumber: this.pageNumber }));
    }
    destroy() {
        this.reset();
    }
    renderLayer(layer = null) {
        if (!this.pdfPage) {
            return;
        }
        if (!this.requiredLayers.has(layer)) {
            return;
        }
        if (layer === PdfViewerPageLayer.PdfCanvas) {
            this.renderPdfCanvas();
        }
        if (layer === PdfViewerPageLayer.PdfText) {
            this.renderPdfText();
        }
        if (layer === PdfViewerPageLayer.PdfAnnotations) {
            this.renderPdfAnnotations();
        }
        if (layer === PdfViewerPageLayer.PdfEmbeddedMedia) {
            this.renderPdfEmbeddedMedia();
        }
        if (layer === PdfViewerPageLayer.Annotations) {
            this.renderAnnotations();
        }
    }
    onPdfLinkAnnotationClick(event) {
        const target = event.target;
        if (target.tagName === 'A' && typeof target.href === 'string' && target.href.indexOf('showpad://') === 0) {
            event.preventDefault();
            this.pdfJsService.pdfJsLinkService.handleShowpadLink(target.href);
        }
    }
    renderErrorMessage() {
        if (!this.errorMessageComponent) {
            this.errorMessageComponent = new ErrorMessageComponent(this.pageElement, {
                disableDownload: this.config.disableDownload,
                downloadUrl: this.config.downloadUrl,
                downloadText: this.config.translations[TranslationKeys.Download],
                errorText: this.config.translations[TranslationKeys.RenderError],
                offlineErrorText: this.config.translations[TranslationKeys.RenderErrorWhenOffline],
                closeText: this.config.translations[TranslationKeys.Close],
                tintColor: this.config.tintColor
            }, this.eventBus);
        }
    }
    destroyErrorMessage() {
        if (this.errorMessageComponent) {
            this.errorMessageComponent.destroy();
            this.errorMessageComponent = null;
        }
    }
    focus() {
        if (this.isFocused) {
            return;
        }
        this.eventBus.dispatch(new PdfViewerFocusChangeEvent({
            pdfViewerPage: PdfViewerPageMapper.mapToPublicPdfViewerPage(this)
        }));
        this.isFocused = true;
        if (this.previewRenderState === RenderState.Finished) {
            this.eventBus.dispatch(new PdfViewerRenderedPreviewFocusEvent({
                pdfViewerPage: PdfViewerPageMapper.mapToPublicPdfViewerPage(this)
            }));
        }
        else {
            this.eventBus.on(PdfViewerEventType.PreviewRendered, this.dispatchOneRenderedPreviewFocusEvent);
        }
        if (this.renderState === RenderState.Finished) {
            this.eventBus.dispatch(new PdfViewerRenderedPageFocusEvent({
                pdfViewerPage: PdfViewerPageMapper.mapToPublicPdfViewerPage(this)
            }));
        }
        else {
            this.eventBus.on(PdfViewerEventType.PageRendered, this.dispatchOneRenderedPageFocusEvent);
        }
    }
    blur() {
        if (!this.isFocused) {
            return;
        }
        this.isFocused = false;
        this.eventBus.off(PdfViewerEventType.PreviewRendered, this.dispatchOneRenderedPreviewFocusEvent);
        this.eventBus.off(PdfViewerEventType.PageRendered, this.dispatchOneRenderedPageFocusEvent);
    }
    get pdfEmbeddedMediaCanvasRenderedAtScale() {
        if (this.pdfEmbeddedMediaLayerComponent) {
            return this.pdfEmbeddedMediaLayerComponent.canvasRenderedAtScale;
        }
        return null;
    }
}
//# sourceMappingURL=pdf-viewer-page.component.js.map